import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { crmApi, errMsg } from '@crm/api';
import { UiAutosuggest, UiButton, UiInput } from '@shared/common/ui';
import { useBranchRealtime } from '@crm/hooks/useBranchRealtime';
import {
  clearCurrentEvent,
  createEventOrder,
  createEventPromocode,
  fetchEventForSale,
  searchEvents,
  selectCurrentEvent,
  selectEventResults,
  selectEventSaveStatus,
} from '@crm/features/events/eventsSlice';

import { HallDK } from './events/halls/HallDK';
import { HallFreeSeats } from './events/halls/HallFreeSeats';
import {
  seatMapBalcony,
  seatMapParter,
  getBalconyRowNumber,
  getParterRowNumber,
} from './events/halls/seatMaps';
import {
  findActivePromocode,
  promocodeDiscount,
  seatPricesWithPromo,
} from './events/promo';

const MAX_SEATS = 5;
const RESERVATION_USER_KEY = 'crm.events.reservationUser';

/**
 * Стабильный идентификатор для резервирования мест. Сохраняется в localStorage,
 * чтобы после перезагрузки страницы холды остались «своими» (иначе места
 * зависают занятыми на весь TTL).
 */
function getReservationUser() {
  try {
    let id = localStorage.getItem(RESERVATION_USER_KEY);
    if (!id) {
      id = `crm_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(RESERVATION_USER_KEY, id);
    }
    return id;
  } catch {
    return `crm_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

function isReservableSeat(seat) {
  return Boolean(seat && seat.type !== 'free_seats' && seat.typeSeat);
}

function getSeatKey(seat) {
  if (!seat) return '';
  return [
    seat.typeSeat || seat.type || '',
    seat.rowIndex ?? seat.row ?? '',
    seat.seatIndex ?? seat.seat ?? '',
    seat.rowNumber ?? '',
    seat.seatLabel ?? '',
    seat.fio ?? '',
  ].join('|');
}

function isSameSeat(a, b) {
  if (!a || !b) return false;
  if (a.type === 'free_seats' || b.type === 'free_seats') {
    return a === b || (a.fio && b.fio && a.fio === b.fio);
  }
  return (
    a.typeSeat === b.typeSeat &&
    Number(a.rowIndex ?? a.row) === Number(b.rowIndex ?? b.row) &&
    Number(a.seatIndex ?? a.seat) === Number(b.seatIndex ?? b.seat)
  );
}

function diffSeats(prev, next) {
  const prevKeys = new Set((prev || []).map(getSeatKey));
  const nextKeys = new Set((next || []).map(getSeatKey));
  return {
    added: (next || []).filter((seat) => !prevKeys.has(getSeatKey(seat))),
    removed: (prev || []).filter((seat) => !nextKeys.has(getSeatKey(seat))),
  };
}

/** Строит карту занятых мест зала DK из оплаченных заказов. */
function buildOccupiedPlaces(orders) {
  const result = {};
  const getSeatLabel = (ticket) => {
    if (ticket.seatLabel != null) return Number(ticket.seatLabel);
    const map = ticket.typeSeat === 'Балкон' ? seatMapBalcony : seatMapParter;
    return Number(map?.[ticket.row]?.[ticket.seat]?.label ?? ticket.seat);
  };
  const getRowKey = (ticket) => {
    if (ticket.rowNumber != null) return Number(ticket.rowNumber);
    const getRowNumber = ticket.typeSeat === 'Балкон' ? getBalconyRowNumber : getParterRowNumber;
    return Number(getRowNumber(ticket.row) ?? ticket.row);
  };
  const addSeat = (ticket, seatValue) => {
    if (!ticket.typeSeat) return;
    if (!result[ticket.typeSeat]) result[ticket.typeSeat] = {};
    const rowKey = getRowKey(ticket);
    if (!result[ticket.typeSeat][rowKey]) result[ticket.typeSeat][rowKey] = [];
    result[ticket.typeSeat][rowKey].push(seatValue);
  };
  for (const order of orders || []) {
    for (const ticket of order.tickets || []) {
      addSeat(ticket, getSeatLabel(ticket));
    }
  }
  return result;
}

function seatLabelText(item) {
  return `${item.typeSeat}, ряд ${item.rowNumber ?? item.row + 1}, место ${item.seatLabel ?? item.seat + 1}`;
}

export function EventsPage() {
  const { branchId } = useParams();
  const dispatch = useDispatch();

  const eventResults = useSelector(selectEventResults);
  const event = useSelector(selectCurrentEvent);
  const saveStatus = useSelector(selectEventSaveStatus);

  const [eventQuery, setEventQuery] = useState('');
  const [selectedEventLabel, setSelectedEventLabel] = useState('');
  const [clientQuery, setClientQuery] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [email, setEmail] = useState('');
  const [promocode, setPromocode] = useState('');
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [reservedSeats, setReservedSeats] = useState({});
  const [notice, setNotice] = useState(null);
  const [lastOrderInfo, setLastOrderInfo] = useState(null);
  const [promoReissueModal, setPromoReissueModal] = useState(null);
  const [promoCreating, setPromoCreating] = useState(false);

  const reservationUser = useRef('');
  if (!reservationUser.current) reservationUser.current = getReservationUser();
  const selectedSeatsRef = useRef(selectedSeats);
  const prevSeatsRef = useRef([]);
  const eventRef = useRef(event);
  const restoredEventRef = useRef(null);

  const eventId = event?.id ?? null;
  const hallType = event?.hallType ?? null;
  const paidOrders = useMemo(
    () => (Array.isArray(event?.orders) ? event.orders.filter((o) => Number(o.statusPayment) === 2) : []),
    [event?.orders],
  );

  useEffect(() => {
    eventRef.current = event;
  }, [event]);

  // ---- Поиск события ----
  useEffect(() => {
    if (selectedEventLabel) return undefined;
    const value = eventQuery.trim();
    if (!value) return undefined;
    const timer = setTimeout(() => {
      dispatch(searchEvents({ branchId, q: value }));
    }, 250);
    return () => clearTimeout(timer);
  }, [eventQuery, selectedEventLabel, branchId, dispatch]);

  const resetSaleState = useCallback(() => {
    setSelectedSeats([]);
    prevSeatsRef.current = [];
    selectedSeatsRef.current = [];
    restoredEventRef.current = null;
    setReservedSeats({});
    setSelectedClient(null);
    setClientQuery('');
    setClientResults([]);
    setEmail('');
    setPromocode('');
    setNotice(null);
    setLastOrderInfo(null);
    setPromoReissueModal(null);
  }, []);

  const onSelectEvent = (item) => {
    setSelectedEventLabel(item.name);
    setEventQuery(item.name);
    resetSaleState();
    dispatch(fetchEventForSale({ branchId, eventId: item.id }));
  };

  const onClearEvent = () => {
    setSelectedEventLabel('');
    setEventQuery('');
    resetSaleState();
    dispatch(clearCurrentEvent());
  };

  // ---- Поиск клиента ----
  useEffect(() => {
    if (selectedClient) return undefined;
    const value = clientQuery.trim();
    if (!value) {
      setClientResults([]);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const { data } = await crmApi(branchId).get('/clients', { params: { q: value, limit: 10 } });
        if (alive) setClientResults(data.items ?? []);
      } catch {
        if (alive) setClientResults([]);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [clientQuery, selectedClient, branchId]);

  const onSelectClient = (client) => {
    setSelectedClient(client);
    setClientQuery(client.fio ?? '');
    setClientResults([]);
    setEmail(client.emails?.[0] ?? '');
    const promo = event?.promoCodes?.find(
      (p) => String(p.userId) === String(client.id) && p.isUse === false,
    );
    if (promo) setPromocode(promo.code);
  };

  const onClearClient = () => {
    setSelectedClient(null);
    setClientQuery('');
    setClientResults([]);
  };

  // ---- Резервирование мест (зал DK) ----
  useEffect(() => {
    selectedSeatsRef.current = selectedSeats;
  }, [selectedSeats]);

  const refreshReserved = useCallback(() => {
    const current = eventRef.current;
    if (!current?.id || current?.hallType !== 'DK') return;
    crmApi(branchId)
      .get(`/events/${current.id}/reservations`, { params: { user: reservationUser.current } })
      .then(({ data }) => {
        setReservedSeats(data.reserved ?? {});
        // После перезагрузки страницы восстанавливаем ранее выбранные (свои) места.
        if (restoredEventRef.current !== current.id) {
          restoredEventRef.current = current.id;
          const mine = Array.isArray(data.mine) ? data.mine : [];
          if (mine.length > 0 && selectedSeatsRef.current.length === 0) {
            prevSeatsRef.current = mine;
            selectedSeatsRef.current = mine;
            setSelectedSeats(mine);
          }
        }
      })
      .catch(() => undefined);
  }, [branchId]);

  const reserveSeat = useCallback(
    (seat) => {
      const current = eventRef.current;
      if (!current?.id || !isReservableSeat(seat)) return;
      crmApi(branchId)
        .post(`/events/${current.id}/reservations`, { user: reservationUser.current, seat })
        .catch((err) => {
          if (err?.response?.data?.details?.seat_closed) {
            setSelectedSeats((list) => list.filter((item) => !isSameSeat(item, seat)));
            refreshReserved();
            setNotice({ type: 'error', text: 'Это место уже выбрал другой пользователь.' });
          }
        });
    },
    [branchId, refreshReserved],
  );

  const releaseSeat = useCallback(
    (seat) => {
      const current = eventRef.current;
      if (!current?.id || !isReservableSeat(seat)) return;
      crmApi(branchId)
        .delete(`/events/${current.id}/reservations`, { data: { user: reservationUser.current, seat } })
        .catch(() => undefined);
    },
    [branchId],
  );

  useEffect(() => {
    const { added, removed } = diffSeats(prevSeatsRef.current, selectedSeats);
    added.filter(isReservableSeat).forEach(reserveSeat);
    removed.filter(isReservableSeat).forEach(releaseSeat);
    prevSeatsRef.current = selectedSeats;
  }, [selectedSeats, reserveSeat, releaseSeat]);

  useEffect(() => {
    if (!eventId || hallType !== 'DK') return undefined;
    refreshReserved();
    const interval = setInterval(refreshReserved, 10000);
    return () => clearInterval(interval);
  }, [eventId, hallType, refreshReserved]);

  useEffect(
    () => () => {
      selectedSeatsRef.current.filter(isReservableSeat).forEach(releaseSeat);
    },
    [releaseSeat],
  );

  useBranchRealtime(branchId, (action) => {
    if (action.startsWith('events:order.') && eventRef.current?.id) {
      dispatch(fetchEventForSale({ branchId, eventId: eventRef.current.id }));
    }
  });

  // ---- Производные значения ----
  const occupiedPlaces = useMemo(() => buildOccupiedPlaces(paidOrders), [paidOrders]);

  const promoCodeObj = useMemo(() => {
    if (!promocode.trim()) return null;
    return findActivePromocode(event?.promoCodes, promocode);
  }, [promocode, event?.promoCodes]);

  const promoInputError = useMemo(() => {
    if (!promocode.trim()) return '';
    if (!promoCodeObj) return 'Промокод недействителен или уже использован';
    if (promocodeDiscount(promoCodeObj) <= 0) return 'У промокода не указана сумма скидки';
    return '';
  }, [promocode, promoCodeObj]);

  const seatPayPrices = useMemo(
    () => seatPricesWithPromo(selectedSeats, event?.promoCodes, promocode),
    [selectedSeats, event?.promoCodes, promocode],
  );

  const getAmountTicket = useCallback(
    (_item, index) => seatPayPrices[index] ?? 0,
    [seatPayPrices],
  );

  const totalAmount = useMemo(
    () => selectedSeats.reduce((sum, item, index) => sum + getAmountTicket(item, index), 0),
    [selectedSeats, getAmountTicket],
  );

  const soldTicketsAmount = useMemo(() => {
    return paidOrders.reduce((sum, order) => {
      const tickets = Array.isArray(order?.tickets) ? order.tickets : [];
      return sum + tickets.reduce((acc, t) => acc + (parseFloat(t?.price) || 0), 0);
    }, 0);
  }, [paidOrders]);

  const deleteSeat = (item, ind) => {
    setSelectedSeats((list) => list.filter((seat, index) => !(index === ind || isSameSeat(seat, item))));
  };

  const requestPromocode = async (force = false) => {
    if (!eventId) return;
    setPromoCreating(true);
    try {
      const res = await dispatch(
        createEventPromocode({
          branchId,
          eventId,
          payload: {
            clientId: selectedClient?.id ?? null,
            email: email.trim() || null,
            force,
          },
        }),
      ).unwrap();
      setPromoReissueModal(null);
      setNotice({
        type: 'success',
        text: `Промокод создан: ${res.promocode} (скидка ${res.discount ?? promocodeDiscount(res.event?.promoCodes?.find((p) => p.code === res.promocode))} ₽)`,
      });
      setPromocode(res.promocode);
    } catch (payload) {
      const details = payload?.details;
      if (details?.promocode_already_issued && details?.existingCode) {
        setPromoReissueModal({ existingCode: details.existingCode });
        return;
      }
      setNotice({
        type: 'error',
        text: payload?.message ?? errMsg(payload, 'Не удалось создать промокод'),
      });
    } finally {
      setPromoCreating(false);
    }
  };

  const onGetPromocode = () => {
    void requestPromocode(false);
  };

  const onSubmitOrder = async (e) => {
    e.preventDefault();
    if (!eventId) return;
    if (selectedSeats.length === 0) {
      setNotice({ type: 'error', text: 'Выберите хотя бы одно место.' });
      return;
    }
    if (!email.trim()) {
      setNotice({ type: 'error', text: 'Укажите email для отправки билетов.' });
      return;
    }
    if (promoInputError) {
      setNotice({ type: 'error', text: promoInputError });
      return;
    }
    try {
      const res = await dispatch(
        createEventOrder({
          branchId,
          eventId,
          payload: {
            clientId: selectedClient?.id ?? null,
            email: email.trim(),
            fio: selectedClient?.fio ?? null,
            phone: selectedClient?.phone ?? null,
            promocode: promocode || null,
            seats: selectedSeats,
            type: hallType === 'freeSeat' ? 'free_seat' : null,
          },
        }),
      ).unwrap();
      setSelectedSeats([]);
      prevSeatsRef.current = [];
      setPromocode('');
      if (res.ticketFile && res.order?.id) {
        setLastOrderInfo({ eventId, orderId: res.order.id, ticketFile: res.ticketFile });
      } else {
        setLastOrderInfo(null);
      }
      setNotice({
        type: 'success',
        text: res.emailSent
          ? 'Заказ оформлен. Билеты отправлены клиенту на email.'
          : 'Заказ оформлен. Не удалось отправить письмо — проверьте настройки почты.',
      });
    } catch (err) {
      setNotice({ type: 'error', text: errMsg(err, 'Не удалось оформить заказ') });
    }
  };

  const downloadTickets = async () => {
    if (!lastOrderInfo) return;
    try {
      const { data } = await crmApi(branchId).get(
        `/events/${lastOrderInfo.eventId}/orders/${lastOrderInfo.orderId}/ticket`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = lastOrderInfo.ticketFile || 'tickets.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setNotice({ type: 'error', text: errMsg(err, 'Не удалось скачать билеты') });
    }
  };

  const saving = saveStatus === 'loading';

  return (
    <section className="events-page">
      <header className="page-header">
        <h1>События</h1>
      </header>

      <div className="form-card form-card--wide events-page__search-card">
        <div className="events-page__search">
          <UiAutosuggest
            label="Мероприятие"
            value={eventQuery}
            onChange={(value) => {
              setEventQuery(value);
              if (selectedEventLabel) {
                setSelectedEventLabel('');
                dispatch(clearCurrentEvent());
                resetSaleState();
              }
            }}
            suggestions={selectedEventLabel ? [] : eventResults}
            onSelect={onSelectEvent}
            onClear={onClearEvent}
            placeholder="Начните вводить название..."
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => `${item.name}${item.dateStart ? ` · ${item.dateStart}` : ''}`}
            emptyText="События не найдены"
          />

          {event && (
            <UiAutosuggest
              label="Клиент"
              value={clientQuery}
              onChange={(value) => {
                if (selectedClient) onClearClient();
                setClientQuery(value);
              }}
              suggestions={selectedClient ? [] : clientResults}
              onSelect={onSelectClient}
              onClear={onClearClient}
              placeholder="Начните вводить имя..."
              getOptionKey={(item) => item.id}
              getOptionLabel={(item) =>
                item.suggestLabel || `${item.fio ?? ''}${item.phone ? ` · ${item.phone}` : ''}`
              }
              emptyText="Клиенты не найдены"
            />
          )}

          {event && (
            <div className="events-page__search-action">
              <UiButton type="button" variant="ghost" onClick={onGetPromocode} disabled={promoCreating}>
                {promoCreating ? 'Создаём…' : 'Получить промокод'}
              </UiButton>
            </div>
          )}
        </div>
      </div>

      {!event && (
        <div className="form-card form-card--wide events-page__placeholder">
          Выберите мероприятие, чтобы открыть схему зала и оформить билеты.
        </div>
      )}

      {event && (
        <div className="events-page__body">
          <div className="form-card form-card--wide events-page__hall">
            {hallType === 'DK' ? (
              <HallDK
                occupiedPlaces={occupiedPlaces}
                reservedSeats={reservedSeats}
                selectedSeats={selectedSeats}
                setSeats={setSelectedSeats}
                closeSelect={false}
                eventData={event}
              />
            ) : (
              <HallFreeSeats
                selectedSeats={selectedSeats}
                setSeats={setSelectedSeats}
                eventData={event}
                value={paidOrders}
              />
            )}
          </div>

          <aside className="form-card form-card--wide events-page__cart">
            <div className="events-page__cart-head">
              <h2 className="events-page__cart-title">Корзина</h2>
              <span className="events-page__sold">
                Продано: <b>{soldTicketsAmount} ₽</b>
              </span>
            </div>

            <div className="events-page__cart-body">
              {notice && (
                <div className={`events-page__notice events-page__notice--${notice.type}`}>{notice.text}</div>
              )}

              {lastOrderInfo && (
                <UiButton type="button" variant="ghost" onClick={downloadTickets}>
                  Скачать билеты (PDF)
                </UiButton>
              )}

              {selectedSeats.length > 0 ? (
                <ul className="events-page__seats">
                  {selectedSeats.map((item, ind) => (
                    <li className="events-page__seat" key={`${getSeatKey(item)}-${ind}`}>
                      <span className="events-page__seat-label">
                        {item.type === 'free_seats' ? item.fio : seatLabelText(item)}
                      </span>
                      <span className="events-page__seat-price">{getAmountTicket(item, ind)} ₽</span>
                      <button
                        type="button"
                        className="events-page__seat-remove"
                        aria-label="Удалить"
                        onClick={() => deleteSeat(item, ind)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="events-page__empty">Выбранные билеты появятся здесь после добавления.</div>
              )}
            </div>

            <div className="events-page__cart-foot">
              {selectedSeats.length > 0 && (
                <div className="events-page__total">
                  <span>Итого</span>
                  <b>{totalAmount} ₽</b>
                </div>
              )}

              <form className="events-page__form" onSubmit={onSubmitOrder}>
                <UiInput
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="mail@example.com"
                />
                <UiInput
                  label="Промокод"
                  value={promocode}
                  onChange={(e) => setPromocode(e.target.value)}
                  placeholder="Необязательно"
                  error={promoInputError}
                />
                {promoCodeObj && !promoInputError && (
                  <div className="events-page__promo-hint">
                    Скидка по промокоду: {promocodeDiscount(promoCodeObj)} ₽
                  </div>
                )}
                <UiButton type="submit" disabled={saving || selectedSeats.length === 0}>
                  {saving ? 'Оформляем…' : `Оформить${selectedSeats.length ? ` · ${totalAmount} ₽` : ''}`}
                </UiButton>
              </form>
            </div>
          </aside>
        </div>
      )}

      {promoReissueModal && (
        <div className="modal-backdrop" role="presentation" onClick={() => setPromoReissueModal(null)}>
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="events-promo-reissue-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal-card__header">
              <h2 id="events-promo-reissue-title">Промокод уже выдан</h2>
              <UiButton variant="ghost" size="sm" type="button" onClick={() => setPromoReissueModal(null)}>
                ×
              </UiButton>
            </header>
            <div className="modal-card__body">
              <p>
                Этому клиенту уже был выдан промокод — <b>{promoReissueModal.existingCode}</b>. Хотите
                выдать еще один?
              </p>
              <div className="modal-card__actions">
                <UiButton type="button" onClick={() => requestPromocode(true)} disabled={promoCreating}>
                  {promoCreating ? 'Создаём…' : 'Да'}
                </UiButton>
                <UiButton variant="ghost" type="button" onClick={() => setPromoReissueModal(null)}>
                  Отмена
                </UiButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
